import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import ProductCard from '../components/ProductCard';
import AedPrice from '../components/AedPrice';
import './Products.css';

const Products = () => {
  const { t, language, isRTL } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeOpen, setStoreOpen] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    search: '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    fetchStoreStatus();
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchStoreStatus = async () => {
    try {
      const response = await fetch('/api/settings/public');
      if (response.ok) {
        const settings = await response.json();
        setStoreOpen(settings.storeOpen);
      }
    } catch (error) {
      console.error('Failed to fetch store status:', error);
      setStoreOpen(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/products/categories');
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);

      const response = await fetch(`/api/products?${params}`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      minPrice: '',
      maxPrice: ''
    });
  };

  const formatPrice = (price) => <AedPrice amount={price} />;

  const getProductName = (product) => {
    if (language === 'ar' && product.nameAr) {
      return product.nameAr;
    }
    return product.name;
  };

  return (
    <div className={`products-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="container">
        <div className="page-header">
          <h1>{t('allProducts')}</h1>
          <p>{t('discoverCollection')}</p>
        </div>

        <div className="products-layout">
          {/* Filters Sidebar */}
          <aside className="filters-sidebar">
            <div className="filter-section">
              <h3>{t('search')}</h3>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder={t('searchProducts')}
                className="form-input"
              />
            </div>

            <div className="filter-section">
              <h3>{t('category')}</h3>
              <select
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="">{t('allCategories')}</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="filter-section">
              <h3>{t('priceRange')}</h3>
              <div className="price-inputs">
                <input
                  type="number"
                  name="minPrice"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  placeholder={t('min')}
                  className="form-input"
                />
                <span>-</span>
                <input
                  type="number"
                  name="maxPrice"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  placeholder={t('max')}
                  className="form-input"
                />
              </div>
            </div>

            <button onClick={clearFilters} className="btn btn-secondary btn-block">
              {t('clearFilters')}
            </button>
          </aside>

          {/* Products Grid */}
          <div className="products-content">
            {storeOpen === null ? (
              <div className="loading">
                <div className="spinner"></div>
              </div>
            ) : !storeOpen ? (
              <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                <div style={{ marginBottom: '16px' }}><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>
                <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                  {language === 'ar' ? 'المتجر مغلق حالياً' : 'Store is currently closed'}
                </h2>
                <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '400px', margin: '0 auto' }}>
                  {language === 'ar'
                    ? 'لا توجد منتجات متاحة حالياً. يرجى العودة لاحقاً.'
                    : 'No items are available at the moment. Please check back later.'}
                </p>
              </div>
            ) : (
              <>
                <div className="products-header">
                  <span>{products.length} {t('productsFound')}</span>
                </div>

                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                  </div>
                ) : products.length === 0 ? (
                  <div className="no-products">
                    <p>{t('noProducts')}</p>
                    <button onClick={clearFilters} className="btn btn-primary">
                      {t('clearFilters')}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-3">
                    {products.map(product => (
                      <ProductCard
                        key={product.id}
                        product={{
                          ...product,
                          displayName: getProductName(product),
                          formattedPrice: formatPrice(product.price)
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
